import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useActiveSpecialties, type SpecialtyDomain } from '@/hooks/useActiveSpecialties';

interface SpecialtyContextValue {
  /** Currently active specialty for the surgeon console session. */
  specialty: SpecialtyDomain;
  setSpecialty: (s: SpecialtyDomain) => void;
  /** All specialties enabled on the tenant (from `clients.active_specialties`). */
  available: SpecialtyDomain[];
  /** True when there are 2+ specialties — display the switcher. */
  showSwitcher: boolean;
  loading: boolean;
}

const SpecialtyContext = createContext<SpecialtyContextValue | null>(null);

const STORAGE_KEY = 'link.surgeon.activeSpecialty';

export function SpecialtyProvider({ children }: { children: ReactNode }) {
  const { loading, specialties, showSwitcher } = useActiveSpecialties();
  const [specialty, setSpecialtyState] = useState<SpecialtyDomain>('orthopedics');

  // Hydrate from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'orthopedics' || stored === 'podiatry') {
      setSpecialtyState(stored);
    }
  }, []);

  // Once tenant specialties load, ensure selection is allowed
  useEffect(() => {
    if (loading) return;
    if (!specialties.includes(specialty)) {
      setSpecialtyState(specialties[0] ?? 'orthopedics');
    }
  }, [loading, specialties, specialty]);

  const setSpecialty = useCallback((s: SpecialtyDomain) => {
    setSpecialtyState(s);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, s);
    }
  }, []);

  return (
    <SpecialtyContext.Provider
      value={{ specialty, setSpecialty, available: specialties, showSwitcher, loading }}
    >
      {children}
    </SpecialtyContext.Provider>
  );
}

export function useSpecialty(): SpecialtyContextValue {
  const ctx = useContext(SpecialtyContext);
  if (!ctx) {
    // Safe fallback: act as orthopedics-only when provider is missing
    return {
      specialty: 'orthopedics',
      setSpecialty: () => {},
      available: ['orthopedics'],
      showSwitcher: false,
      loading: false,
    };
  }
  return ctx;
}
